// scroll-manager.js
import Lenis from 'lenis';

let lenisInstance;

// Keep track of the requestAnimationFrame ID to cancel it if needed
let rafId;
let isAnchorScrollingListenerAttached = false;

/**
 * RequestAnimationFrame callback to update Lenis.
 * @param {number} time - The current time.
 */
function raf(time) {
  if (lenisInstance) {
    lenisInstance.raf(time);
  }
  rafId = requestAnimationFrame(raf);
}

/**
 * Initializes the Lenis smooth scrolling instance.
 * @param {object} options - Lenis constructor options.
 * @returns {Lenis|null} The Lenis instance, or null if it couldn't be initialized.
 */
export function initializeScrollManager(options = {}) {
  if (lenisInstance) {
    // console.warn('Scroll manager (Lenis) already initialized.');
    return lenisInstance;
  }

  try {
    lenisInstance = new Lenis(options);
    // console.log('Scroll manager (Lenis) initialized.');
    
    // Start the animation frame loop
    if (rafId) cancelAnimationFrame(rafId); // Clear any existing loop
    rafId = requestAnimationFrame(raf);
  } catch (error) {
    console.error("Failed to initialize Lenis:", error);
    return null;
  }
  
  return lenisInstance;
}

export function getLenisInstance() {
  return lenisInstance;
}

export function destroyScrollManager() {
  if (lenisInstance) {
    lenisInstance.destroy(); // Lenis's internal cleanup
    lenisInstance = null;
  }
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  // console.log('Scroll manager (Lenis) destroyed.');
}

/**
 * Calculates the offset for the header.
 * @returns {number} The negative height of the header, or 0 if not found.
 */
function getHeaderOffset() {
  const header = document.getElementById('header');
  const wpAdminBar = document.getElementById('wpadminbar');
  let offset = 0;
  if (header) offset += header.offsetHeight;
  if (wpAdminBar) offset += wpAdminBar.offsetHeight;
  return -offset;
}

/**
 * Initializes handling for same-page anchor links to scroll smoothly using Lenis.
 * @param {Lenis} lenisInstance - The initialized Lenis instance.
 */
export function initializeLenisAnchorScrolling(lenisInstance) {
  if (!lenisInstance) {
    console.error('Lenis instance not provided for anchor scrolling initialization.');
    return;
  }


  if (!isAnchorScrollingListenerAttached) {
    document.addEventListener('click', function(event) {
    // If the clicked element or an ancestor has 'data-scroll-ignore',
    // we need to stop both our script and the default link behavior.
    if (event.target.closest('[data-scroll-ignore]')) {
      // If the ignored element is inside ANY link, we must prevent the link's default action
      // (e.g., browser navigation or a Barba.js transition) to allow the inner element
      // (like a slideshow button) to function without triggering the parent link.
      if (event.target.closest('a')) {
        event.preventDefault();
      }
      // In either case, we stop the scroll manager from proceeding.
      return;
    }

    // Find the closest ancestor anchor link
    const link = event.target.closest('a');
  
    if (!link) return;

    const href = link.getAttribute('href');  
    // Must have a hash to be considered for anchor scrolling
    if (!href || href.indexOf('#') === -1) return;

    // Resolve the URL to check if it points to the current page
    const linkUrl = new URL(link.href, window.location.href);

    // Normalize pathnames to handle trailing slashes consistently
    const currentPath = window.location.pathname.replace(/\/$/, "") || '/';
    const linkPath = linkUrl.pathname.replace(/\/$/, "") || '/';

    // Check if origin and pathname match the current page
    if (linkUrl.origin !== window.location.origin || linkPath !== currentPath || linkUrl.search !== window.location.search) {
      return; // Different page, let browser/Barba handle it
    }

    const hash = linkUrl.hash;
    let targetToScroll;
    const scrollOptions = {
      lock: true, // Prevent user scroll during programmatic scroll
    };

    // Handle links like href="#" or href="#top" to scroll to the top of the page
    if (!hash || hash === '#' || hash.toLowerCase() === '#top') {
      targetToScroll = 0;
    } else {
      // For other hashes, check if the target element exists on the page.
      try {
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          scrollOptions.offset = getHeaderOffset();
          targetToScroll = hash; // Use the selector string; Lenis will resolve it.
        } else {
          console.warn(`Anchor target "${hash}" not found on this page. Default link behavior will proceed.`);
          return;
        }
      } catch (e) {
        console.warn(`Invalid selector "${hash}".`);
        return;
      }
    }

    // If a valid target for Lenis is determined (0 or an existing selector)
    event.preventDefault(); // Prevent the default anchor jump
    lenisInstance.scrollTo(targetToScroll, scrollOptions);

    // Manually update URL hash if needed, as preventDefault stops it.
    if (targetToScroll !== 0 && history.pushState) {
      history.pushState(null, null, hash);
    }
    }, false); // Use event delegation on the document
    isAnchorScrollingListenerAttached = true;
  }
}

/**
 * Handles scrolling to the URL hash on page load or transition.
 * @param {Lenis} lenisInstance 
 * @param {string|null} hash - Optional hash to scroll to.
 */
export function handleHashScroll(lenisInstance, hash = null) {
  if (!lenisInstance) return;

  const targetHash = hash || window.location.hash;

  if (targetHash && targetHash !== '#') {
    // Use setTimeout to ensure the DOM is fully settled and painted after the heavy Barba transition
    setTimeout(() => {
      try {
        const targetElement = document.querySelector(targetHash);
        if (targetElement) {
          const offset = getHeaderOffset();
          lenisInstance.scrollTo(targetHash, {
            offset: offset,
            force: true,
            lock: true,
          });
        }
      } catch (e) {
        // Invalid selector, ignore
      }
    }, 500);
  }
}